import React, { useState, useEffect } from 'react';
import {
  Play,
  Pause,
  X,
  Gift,
  Star,
  Crown,
  Zap,
  Award,
  Clock,
  Volume2,
  VolumeX,
  CheckCircle,
  Sparkles,
  Timer,
  Coins
} from 'lucide-react';

const RewardedAd = ({
  isVisible,
  onClose,
  onRewardEarned,
  onAdCompleted,
  rewardType = 'premium-features', // 'premium-features', 'customization', 'badges', 'coins'
  trigger = 'user-initiated'
}) => {
  const [currentAd, setCurrentAd] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [hasCompletedMinimum, setHasCompletedMinimum] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [canClaim, setCanClaim] = useState(false);

  // Rewarded ad content with different reward types
  const rewardedAds = {
    'premium-features': {
      id: 'rewarded-premium-1',
      title: 'Débloquez Likico Pro',
      description: 'Regardez cette publicité pour débloquer 7 jours gratuits de fonctionnalités premium',
      brand: 'Likico Pro',
      duration: 30,
      minimumViewTime: 20, // Must watch at least 20 seconds
      reward: {
        type: 'premium-trial',
        value: '7 jours gratuits',
        description: 'Accès complet aux fonctionnalités premium',
        icon: Crown,
        color: '#fbbf24',
        features: [
          'Analyses avancées',
          'Rapports détaillés',
          'Support prioritaire',
          'Personnalisation complète'
        ]
      },
      adContent: {
        videoThumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiPjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiByeD0iMTYiIGZpbGw9IiNmYmJmMjQiLz48Y2lyY2xlIGN4PSIxNjAiIGN5PSI5MCIgcj0iMzAiIGZpbGw9IiNmNTk1MDAiLz48cGF0aCBkPSJtMTQ1IDgwIDIwIDEwLTIwIDEweiIgZmlsbD0id2hpdGUiLz48dGV4dCB4PSIxNjAiIHk9IjE0NSIgZm9udC1zaXplPSIxOCIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IiNkOTc3MDYiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkxpa2lsaW1iYSBQcm88L3RleHQ+PC9zdmc+',
        title: 'Découvrez les Fonctionnalités Premium',
        subtitle: 'Maximisez votre potentiel d\'épargne'
      }
    },
    'customization': {
      id: 'rewarded-custom-1',
      title: 'Personnalisez votre Groupe',
      description: 'Regardez cette publicité pour débloquer des options de personnalisation uniques',
      brand: 'Likico',
      duration: 25,
      minimumViewTime: 18,
      reward: {
        type: 'customization-pack',
        value: 'Pack Personnalisation',
        description: 'Thèmes et badges exclusifs pour votre groupe',
        icon: Sparkles,
        color: '#8b5cf6',
        features: [
          '5 thèmes exclusifs',
          '10 badges personnalisés',
          'Couleurs personnalisées',
          'Logo de groupe'
        ]
      },
      adContent: {
        videoThumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiPjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiByeD0iMTYiIGZpbGw9IiNmM2Y0ZjYiLz48Y2lyY2xlIGN4PSIxMDAiIGN5PSI2MCIgcj0iMjAiIGZpbGw9IiM4YjVjZjYiLz48Y2lyY2xlIGN4PSIxNjAiIGN5PSI5MCIgcj0iMjAiIGZpbGw9IiNlYjI0OGUiLz48Y2lyY2xlIGN4PSIyMDAiIGN5PSI1MCIgcj0iMjAiIGZpbGw9IiMwNjYzZjciLz48cGF0aCBkPSJtMTQ1IDkwIDEwLTUgMTAgNS0xMCA1eiIgZmlsbD0iI2Y5NzMxNiIvPjx0ZXh0IHg9IjE2MCIgeT0iMTUwIiBmb250LXNpemU9IjE2IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iIzM3NDE1MSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+UGVyc29ubmFsaXNhdGlvbjwvdGV4dD48L3N2Zz4=',
        title: 'Exprimez votre Style',
        subtitle: 'Rendez votre groupe unique'
      }
    },
    'badges': {
      id: 'rewarded-badges-1',
      title: 'Collectionnez des Badges',
      description: 'Regardez cette publicité pour gagner un badge exclusif et des points de réputation',
      brand: 'Likico Rewards',
      duration: 20,
      minimumViewTime: 15,
      reward: {
        type: 'achievement-badge',
        value: 'Badge "Supporter"',
        description: 'Badge exclusif + 500 points de réputation',
        icon: Award,
        color: '#f59e0b',
        features: [
          'Badge "Supporter" rare',
          '500 points de réputation',
          'Statut privilégié',
          'Reconnaissance communauté'
        ]
      },
      adContent: {
        videoThumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiPjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiByeD0iMTYiIGZpbGw9IiNmZmY3ZWQiLz48Y2lyY2xlIGN4PSIxNjAiIGN5PSI4MCIgcj0iMzUiIGZpbGw9IiNmNTllMGIiLz48cGF0aCBkPSJNMTYwIDUwIEwxNzAgNzAgTDE5MCA3MCBMMTY1IDkwIEwxNzUgMTEwIEwxNjAgOTUgTDE0NSAxMTAgTDE1NSA5MCBMMTM5IDcwIEwxNTAgNzB6IiBmaWxsPSJ3aGl0ZSIvPjx0ZXh0IHg9IjE2MCIgeT0iMTQ1IiBmb250LXNpemU9IjE2IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iI2Q5NzcwNCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+QmFkZ2UgU3VwcG9ydGVyPC90ZXh0Pjwvc3ZnPg==',
        title: 'Badge Exclusif',
        subtitle: 'Montrez votre engagement'
      }
    },
    'coins': {
      id: 'rewarded-coins-1',
      title: 'Gagnez des Pièces',
      description: 'Regardez cette publicité pour gagner 100 pièces utilisables dans l\'application',
      brand: 'Likico Coins',
      duration: 15,
      minimumViewTime: 12,
      reward: {
        type: 'virtual-coins',
        value: '100 Pièces',
        description: 'Utilisez vos pièces pour débloquer des fonctionnalités',
        icon: Coins,
        color: '#10b981',
        features: [
          '100 pièces instant',
          'Utilisables immédiatement',
          'Pas d\'expiration',
          'Bonus de fidélité'
        ]
      },
      adContent: {
        videoThumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiPjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiByeD0iMTYiIGZpbGw9IiNlY2ZkZjUiLz48Y2lyY2xlIGN4PSIxMjAiIGN5PSI3MCIgcj0iMjAiIGZpbGw9IiMxMGI5ODEiLz48Y2lyY2xlIGN4PSIxNjAiIGN5PSI4NSIgcj0iMjQiIGZpbGw9IiMwNTk2NTEiLz48Y2lyY2xlIGN4PSIyMDAiIGN5PSI2NSIgcj0iMTgiIGZpbGw9IiMxMGI5ODEiLz48dGV4dCB4PSIxNjAiIHk9IjkzIiBmb250LXNpemU9IjE2IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPjEwMDwvdGV4dD48dGV4dCB4PSIxNjAiIHk9IjE0NSIgZm9udC1zaXplPSIxNiIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IiMwNTk2NTEiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkdhZ25leiBkZXMgUGnDqGNlczwvdGV4dD48L3N2Zz4=',
        title: 'Récompenses Instantanées',
        subtitle: '100 pièces à gagner'
      }
    }
  };

  useEffect(() => {
    if (isVisible && rewardType) {
      const ad = rewardedAds[rewardType];
      if (ad) {
        setCurrentAd(ad);
        setTimeLeft(ad.duration);
      }
    }
  }, [isVisible, rewardType]);

  useEffect(() => {
    let timer;
    if (isPlaying && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          const newTime = prev - 1;
          const newProgress = ((currentAd.duration - newTime) / currentAd.duration) * 100;
          setProgress(newProgress);
          
          // Check if minimum view time reached
          if (currentAd.duration - newTime >= currentAd.minimumViewTime) {
            setHasCompletedMinimum(true);
          }
          
          // Ad completed
          if (newTime <= 0) {
            setIsPlaying(false);
            setShowReward(true);
            setCanClaim(true);
            if (onAdCompleted) onAdCompleted(currentAd);
            return 0;
          }
          
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isPlaying, timeLeft, currentAd, onAdCompleted]);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleClose = () => {
    if (hasCompletedMinimum || timeLeft === 0) {
      setIsPlaying(false);
      onClose();
    }
  };

  const handleClaimReward = () => {
    if (canClaim && onRewardEarned && currentAd) {
      onRewardEarned(currentAd.reward);
      onClose();
    }
  };

  const handleSkip = () => {
    if (hasCompletedMinimum) {
      setShowReward(true);
      setCanClaim(true);
      setIsPlaying(false);
    }
  };

  if (!isVisible || !currentAd) return null;

  const RewardIcon = currentAd.reward.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm">
      <div className="relative max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 pb-8">
          {/* Close button - only show if minimum time reached */}
          {hasCompletedMinimum && (
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          <div className="text-center">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Gift className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-bold mb-2">{currentAd.title}</h2>
            <p className="text-purple-100 text-sm">{currentAd.description}</p>
          </div>
        </div>

        {showReward ? (
          /* Reward Screen */
          <div className="p-8 text-center">
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce"
              style={{ backgroundColor: currentAd.reward.color + '20' }}
            >
              <RewardIcon 
                className="w-10 h-10" 
                style={{ color: currentAd.reward.color }}
              />
            </div>
            
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              🎉 Récompense Gagnée !
            </h3>
            <p className="text-lg font-semibold mb-2" style={{ color: currentAd.reward.color }}>
              {currentAd.reward.value}
            </p>
            <p className="text-gray-600 mb-6">{currentAd.reward.description}</p>
            
            {/* Reward Features */}
            <div className="space-y-2 mb-8">
              {currentAd.reward.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{feature}</span>
                </div>
              ))}
            </div>
            
            <button
              onClick={handleClaimReward}
              disabled={!canClaim}
              className="w-full py-4 px-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105"
            >
              Récupérer ma Récompense
            </button>
          </div>
        ) : (
          /* Ad Viewing Screen */
          <div className="p-6">
            {/* Video/Ad Content Area */}
            <div className="relative mb-6">
              <div 
                className="w-full h-48 bg-gray-100 rounded-xl overflow-hidden cursor-pointer"
                onClick={isPlaying ? handlePause : handlePlay}
              >
                <img 
                  src={currentAd.adContent.videoThumbnail} 
                  alt={currentAd.adContent.title}
                  className="w-full h-full object-cover"
                />
                
                {/* Play/Pause Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                  <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                    {isPlaying ? (
                      <Pause className="w-8 h-8 text-gray-800" />
                    ) : (
                      <Play className="w-8 h-8 text-gray-800 ml-1" />
                    )}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="absolute bottom-2 left-2 right-2">
                <div className="w-full bg-black bg-opacity-30 rounded-full h-2 overflow-hidden">
                  <div 
                    className="h-full bg-white rounded-full transition-all duration-1000 ease-linear"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* Timer */}
              <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-sm flex items-center space-x-1">
                <Timer className="w-3 h-3" />
                <span>{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span>
              </div>

              {/* Mute Button */}
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="absolute top-2 left-2 w-8 h-8 bg-black bg-opacity-60 text-white rounded-full flex items-center justify-center hover:bg-opacity-80 transition-colors"
              >
                {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
            </div>

            {/* Ad Information */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-1">{currentAd.adContent.title}</h3>
              <p className="text-gray-600 text-sm mb-2">{currentAd.adContent.subtitle}</p>
              <p className="text-xs text-gray-500">Sponsorisé par {currentAd.brand}</p>
            </div>

            {/* Reward Preview */}
            <div 
              className="p-4 rounded-xl border-2 border-dashed mb-6"
              style={{ 
                borderColor: currentAd.reward.color,
                backgroundColor: currentAd.reward.color + '08'
              }}
            >
              <div className="flex items-center space-x-3">
                <RewardIcon 
                  className="w-8 h-8" 
                  style={{ color: currentAd.reward.color }}
                />
                <div>
                  <p className="font-semibold text-gray-900">{currentAd.reward.value}</p>
                  <p className="text-sm text-gray-600">À débloquer après visionnage</p>
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between">
              <button
                onClick={handlePlay}
                disabled={isPlaying}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Play className="w-4 h-4" />
                <span>{isPlaying ? 'En lecture...' : 'Regarder'}</span>
              </button>

              {hasCompletedMinimum && (
                <button
                  onClick={handleSkip}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Passer au résultat
                </button>
              )}
            </div>

            {/* Minimum time notice */}
            {!hasCompletedMinimum && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Clock className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-800">
                    Regardez au moins {currentAd.minimumViewTime} secondes pour gagner votre récompense.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardedAd;